import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";

const RANGES = [
  { k: "today", label: "Today" },
  { k: "7d", label: "Last 7 days" },
  { k: "30d", label: "Last 30 days" },
  { k: "custom", label: "Custom range" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

const val = (m, kind) => {
  if (!m) return "—";
  const v = m.value;
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (kind === "money") return `$${Number(v).toLocaleString()}`;
  if (kind === "pct") return `${v}%`;
  return Number(v).toLocaleString();
};

function Metric({ label, m, kind, testid, big, hint }) {
  return (
    <div data-testid={testid} className="border-b border-r border-line p-6">
      <p className="text-[10px] uppercase tracking-[0.2em] text-ink-soft leading-relaxed">{label}</p>
      <p className={`mt-3 font-serif font-light text-ink ${big ? "text-5xl" : "text-4xl"}`}>{val(m, kind)}</p>
      {hint && <p className="mt-2 text-[10px] text-ink-soft/70 font-light leading-relaxed">{hint}</p>}
    </div>
  );
}

function BarRow({ label, count, max, testid }) {
  return (
    <div className="flex items-center gap-4 px-5 py-3" data-testid={testid}>
      <span className="text-sm text-ink w-56 shrink-0 font-light truncate" title={label}>{label}</span>
      <div className="flex-1 h-2 bg-surface"><div className="h-2 bg-accent" style={{ width: `${(count / max) * 100}%` }} /></div>
      <span className="text-sm text-ink-soft w-12 text-right tabular-nums">{Number(count).toLocaleString()}</span>
    </div>
  );
}

export default function PerformanceDashboard() {
  const [range, setRange] = useState("30d");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [sched, setSched] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/admin/performance/schedule-status").then((r) => setSched(r.data)).catch(() => {}); }, []);

  const load = (r, s, e) => {
    setLoading(true);
    let url = `/admin/performance?range=${r}`;
    if (r === "custom") { if (!s || !e) { setLoading(false); return; } url += `&start=${s}&end=${e}`; }
    api.get(url).then((res) => setData(res.data)).catch((err) => toast.error(apiError(err))).finally(() => setLoading(false));
  };
  useEffect(() => { if (range !== "custom") load(range); }, [range]);

  const previewReport = () => window.open(`${api.defaults.baseURL}/admin/performance/weekly-report-preview?auth=${localStorage.getItem("token")}`, "_blank", "noopener");
  const sendReport = async () => {
    if (!window.confirm("Send a one-off test of the weekly performance email now?")) return;
    setSending(true);
    try { const { data: d } = await api.post("/admin/performance/send-weekly-report"); toast.success(`Test report sent to ${d.recipient}.`); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const w = data?.website, f = data?.funnel, products = data?.products || [];
  const g = data?.guide, c = data?.course, a = data?.attribution, h = data?.hurricane, l = data?.list_growth;
  const maxOpens = Math.max(1, ...((c?.resources || []).map((r) => r.opens)));
  const maxPage = Math.max(1, ...((w?.top_pages || []).map((p) => p.views)));
  const maxSource = Math.max(1, ...((w?.sources || []).map((s) => s.views)));
  const devTotal = w ? (w.devices.mobile + w.devices.desktop + w.devices.tablet) : 0;

  const Steps = () => {
    if (!f) return null;
    const steps = [
      ["Visitors", f.visitors.value],
      ["Product Views", f.product_views.value],
      ["Clicked to Buy", f.cta_clicks.value],
      ["Started Checkout", f.checkout_starts.value],
      ["Purchases", f.purchases.value],
    ];
    return (
      <div className="grid grid-cols-2 sm:grid-cols-5 border-t border-l border-line" data-testid="salotto-funnel">
        {steps.map(([lbl, v], i) => (
          <div key={lbl} className="border-b border-r border-line p-5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">{`0${i + 1} · ${lbl}`}</p>
            <p className="mt-2 font-serif font-light text-3xl text-ink">{Number(v ?? 0).toLocaleString()}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="performance-dashboard">
      {/* Range + report controls */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-2 items-center">
          {RANGES.map((r) => (
            <button key={r.k} data-testid={`range-${r.k}`} onClick={() => setRange(r.k)}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${range === r.k ? "bg-ink text-bg border-ink" : "border-line text-ink-soft hover:border-ink hover:text-ink"}`}>
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <button data-testid="preview-weekly-report" onClick={previewReport} className="border border-line px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-ink-soft hover:border-ink hover:text-ink">Preview weekly email</button>
          <button data-testid="send-weekly-report" onClick={sendReport} disabled={sending} className="border border-ink px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bg transition-colors disabled:opacity-60">{sending ? "Sending…" : "Send test email"}</button>
        </div>
      </div>

      {range === "custom" && (
        <div className="flex flex-wrap items-end gap-3 mb-6 border border-line p-4" data-testid="custom-range-controls">
          <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft flex flex-col gap-1">From
            <input type="date" data-testid="custom-start" value={start} onChange={(e) => setStart(e.target.value)} className="border border-line px-3 py-2 text-sm text-ink bg-white" />
          </label>
          <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft flex flex-col gap-1">To
            <input type="date" data-testid="custom-end" value={end} onChange={(e) => setEnd(e.target.value)} className="border border-line px-3 py-2 text-sm text-ink bg-white" />
          </label>
          <button data-testid="custom-apply" onClick={() => load("custom", start, end)} className="bg-ink text-bg px-5 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E]">Apply</button>
        </div>
      )}

      {sched && (
        <p className="mb-6 text-xs text-ink-soft font-light leading-relaxed" data-testid="weekly-schedule-status">
          <span className="text-ink">Weekly report</span> · Enabled: <span className="text-ink" data-testid="schedule-enabled">{sched.enabled ? "Yes" : "No"}</span>
          {" · "}Recipient: <span className="text-ink" data-testid="schedule-recipient">{sched.recipient || "—"}</span>
          {" · "}Schedule: <span className="text-ink">{sched.schedule}</span>
          {" · "}Next scheduled run: <span className="text-ink" data-testid="schedule-next-run">{sched.next_run_label || "—"}</span>
          {sched.enabled && sched.environment === "preview" && <span> (not sent from preview — production only)</span>}
        </p>
      )}

      {data && (
        <p className="mb-8 text-xs text-ink-soft font-light leading-relaxed max-w-3xl" data-testid="data-notice">
          <span className="text-ink">Sales, revenue, leads and email-list figures</span> reflect your full history.
          <span className="text-ink"> Website visitors, sessions, page views and traffic sources</span>{" "}
          {data.page_view_since
            ? <>began accumulating {fmtDate(data.page_view_since)} (first recorded page view) — earlier periods have no visitor data and are never estimated.</>
            : <>will begin accumulating from the first recorded page view — none yet.</>}
          {" "}Your own admin activity and known test traffic are excluded.
        </p>
      )}

      {loading && <p className="py-16 text-center text-sm text-ink-soft">Loading analytics…</p>}

      {!loading && data && (
        <div className="space-y-14">
          {/* At a glance */}
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent mb-4">At a glance · {data.range_label}</p>
            <div className="border-t border-l border-line grid sm:grid-cols-2 lg:grid-cols-4">
              <Metric testid="kpi-revenue" label="Revenue" m={f?.revenue} kind="money" big />
              <Metric testid="kpi-purchases" label="Purchases" m={f?.purchases} big />
              <Metric testid="kpi-visitors" label="Website Visitors" m={w?.visitors} big hint="Since tracking began" />
              <Metric testid="kpi-conversion" label="View → Purchase" m={f?.view_to_purchase_rate} kind="pct" big />
            </div>
          </div>

          {/* Il Salotto Digitale funnel */}
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent mb-4">Il Salotto Digitale · Sales funnel</p>
            <Steps />
            <div className="mt-4 flex flex-wrap gap-x-10 gap-y-2 text-sm text-ink-soft font-light">
              <span>Revenue <span className="text-ink font-normal">{val(f?.revenue, "money")}</span></span>
              <span>View → purchase <span className="text-ink font-normal">{val(f?.view_to_purchase_rate, "pct")}</span></span>
            </div>
          </div>

          {/* Products */}
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent mb-4">Products · individual performance</p>
            <div className="space-y-6" data-testid="product-breakdown">
              {products.map((p) => (
                <div key={p.id} className="border border-line" data-testid={`product-${p.id}`}>
                  <div className="flex items-baseline justify-between gap-3 px-6 py-4 border-b border-line bg-surface/40">
                    <h4 className="font-serif text-xl text-ink">{p.name}</h4>
                    <span className="text-sm text-ink-soft">${p.price}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
                    <ProdCell label="Product Views" value={p.views} />
                    <ProdCell label="Unique Viewers" value={p.unique_viewers} />
                    <ProdCell label="Clicked to Buy" value={p.cta_clicks} />
                    <ProdCell label="Started Checkout" value={p.checkout_starts} />
                    <ProdCell label="Purchases" value={p.purchases} />
                    <ProdCell label="Revenue" value={p.revenue} money />
                  </div>
                  <div className="px-6 py-3 border-t border-line text-sm text-ink-soft font-light flex flex-wrap gap-x-8 gap-y-1">
                    <span>View → purchase conversion: <span className="text-ink">{p.conversion === null || p.conversion === undefined ? "—" : `${p.conversion}%`}</span></span>
                    {p.promo_purchases > 0 && (
                      <span data-testid={`promo-${p.id}`}>Purchases with a promotion code: <span className="text-ink">{p.promo_purchases}</span> · discounts <span className="text-ink">${Number(p.promo_discounts).toLocaleString()}</span> · revenue shown is the amount actually paid</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Website */}
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent mb-1">Website</p>
            <p className="text-xs text-ink-soft font-light mb-4">Accumulating since tracking began · anonymous &amp; cookieless</p>
            <div className="border-t border-l border-line grid sm:grid-cols-2 lg:grid-cols-4">
              <Metric testid="web-visitors" label="Unique Visitors" m={w?.visitors} />
              <Metric testid="web-sessions" label="Sessions" m={w?.sessions} />
              <Metric testid="web-pageviews" label="Page Views" m={w?.page_views} />
              <Metric testid="web-returning" label="New / Returning" m={{ value: `${w?.new_visitors.value ?? 0} / ${w?.returning_visitors.value ?? 0}` }} />
            </div>

            <div className="grid lg:grid-cols-3 gap-10 mt-8">
              <div className="lg:col-span-1">
                <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-3">Most visited pages</p>
                <div className="border border-line divide-y divide-line" data-testid="top-pages">
                  {(w?.top_pages || []).length ? w.top_pages.map((p) => (
                    <BarRow key={p.page} label={p.page} count={p.views} max={maxPage} testid={`page-${p.page}`} />
                  )) : <p className="px-5 py-4 text-sm text-ink-soft font-light">No page-view data yet.</p>}
                </div>
              </div>
              <div className="lg:col-span-1">
                <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-3">Traffic sources</p>
                <div className="border border-line divide-y divide-line" data-testid="traffic-sources">
                  {(w?.sources || []).length ? w.sources.map((s) => (
                    <BarRow key={s.source} label={s.source} count={s.views} max={maxSource} testid={`source-${s.source}`} />
                  )) : <p className="px-5 py-4 text-sm text-ink-soft font-light">No source data yet.</p>}
                </div>
              </div>
              <div className="lg:col-span-1">
                <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-3">Device</p>
                <div className="border border-line divide-y divide-line" data-testid="device-split">
                  {devTotal ? (
                    [["Desktop", w.devices.desktop], ["Mobile", w.devices.mobile], ["Tablet", w.devices.tablet]]
                      .filter(([, v]) => v > 0)
                      .map(([lbl, v]) => (
                        <div key={lbl} className="flex items-center justify-between px-5 py-3">
                          <span className="text-sm text-ink font-light">{lbl}</span>
                          <span className="text-sm text-ink-soft">{v.toLocaleString()} · {Math.round((v / devTotal) * 100)}%</span>
                        </div>
                      ))
                  ) : <p className="px-5 py-4 text-sm text-ink-soft font-light">No device data yet.</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Campaigns */}
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent mb-1">Campaigns</p>
            <p className="text-xs text-ink-soft font-light mb-4">Links tagged with <span className="text-ink">utm_campaign</span> · anonymous first-touch attribution for marketing analysis</p>
            <div className="border border-line overflow-x-auto" data-testid="campaigns">
              {(data.campaigns || []).length ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.18em] text-ink-soft border-b border-line">
                      <th className="text-left font-normal px-5 py-3">Campaign</th>
                      {["Visitors", "Sessions", "Product Views", "Clicked to Buy", "Checkout Starts", "Attributed Purchases", "Attributed Revenue"].map((h) => (
                        <th key={h} className="text-right font-normal px-4 py-3 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {data.campaigns.map((r) => (
                      <tr key={r.campaign} data-testid={`campaign-${r.campaign}`}>
                        <td className="px-5 py-3 text-ink font-light">{r.campaign}</td>
                        {[r.visitors, r.sessions, r.product_views, r.cta_clicks, r.checkout_starts].map((v, i) => (
                          <td key={i} className="px-4 py-3 text-right text-ink-soft tabular-nums">{Number(v).toLocaleString()}</td>
                        ))}
                        <td className="px-4 py-3 text-right text-ink-soft tabular-nums italic">{Number(r.purchases).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-ink-soft tabular-nums italic">≈ ${Number(r.revenue).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="px-5 py-4 text-sm text-ink-soft font-light">No campaign traffic yet. Add <span className="text-ink">?utm_campaign=your-campaign-name</span> to links you share.</p>}
            </div>
            <p className="mt-2 text-[11px] text-ink-soft font-light leading-relaxed max-w-3xl" data-testid="campaigns-note">
              Campaign attribution is based on anonymous first-touch browser activity. Financial totals and confirmed purchases in the main dashboard come from actual payment transactions and remain the source of truth.
              Attributed figures may differ from transaction totals — for example when a buyer returns on another device or browser, has cleared local storage, or is later refunded.
            </p>
          </div>

          {/* Free guide */}
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent mb-1">Complimentary Guide</p>
            <h3 className="font-serif text-2xl text-ink mb-4">Is Estate Management Actually for Me?</h3>
            <div className="border-t border-l border-line grid sm:grid-cols-2 lg:grid-cols-3">
              <Metric testid="guide-landing-views" label="Landing page views" m={g?.landing_views} />
              <Metric testid="guide-requests" label="Guide requests / signups" m={g?.requests} />
              <Metric testid="guide-kit" label="Email captures (Kit)" m={g?.kit_captures} />
              <Metric testid="guide-unique-opens" label="Unique guide opens" m={g?.unique_opens} />
              <Metric testid="guide-repeat-opens" label="Repeat guide opens" m={g?.repeat_opens} />
              <Metric testid="guide-cta" label="Course 1 CTA clicks" m={g?.cta_clicks} />
            </div>
          </div>

          {/* Course 1 resources */}
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent mb-1">Paid Course · library usage</p>
            <h3 className="font-serif text-2xl text-ink mb-4">How to Start as an Estate Manager</h3>
            <div className="border border-line divide-y divide-line" data-testid="resource-usage">
              {(c?.resources || []).map((r) => (
                <BarRow key={r.name} label={r.name} count={r.opens} max={maxOpens} testid={`resource-${r.name}`} />
              ))}
            </div>
            <p className="mt-2 text-[11px] text-ink-soft font-light">A resource open is not a purchase — these count how often buyers open each file.</p>
          </div>

          {/* Attribution + list growth */}
          <div className="grid lg:grid-cols-2 gap-14">
            <div>
              <p className="text-[11px] uppercase tracking-ultra text-accent mb-4">Guide → Course 1 conversion</p>
              <div className="border-t border-l border-line grid grid-cols-3">
                <Metric testid="attr-recipients" label="Guide recipients" m={a?.guide_recipients_total} />
                <Metric testid="attr-purchasers" label="Became customers" m={a?.purchasers_from_guide} />
                <Metric testid="attr-rate" label="Conversion" m={a?.guide_to_course_rate} kind="pct" />
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-ultra text-accent mb-4">Email-list growth (Kit)</p>
              <div className="border-t border-l border-line grid grid-cols-3">
                <Metric testid="list-new" label="New" m={l?.new} />
                <Metric testid="list-unsub" label="Unsubscribed" m={l?.unsubscribed} />
                <Metric testid="list-net" label="Net" m={l?.net} />
              </div>
            </div>
          </div>

          {/* Hurricane */}
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent mb-4">Hurricane Preparedness Guide</p>
            <div className="border-t border-l border-line grid grid-cols-2 max-w-md">
              <Metric testid="hurricane-requests" label="Requests" m={h?.requests} />
              <Metric testid="hurricane-opens" label="Opens / downloads" m={h?.opens} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProdCell({ label, value, money }) {
  return (
    <div className="border-b border-r border-line p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">{label}</p>
      <p className="mt-2 font-serif font-light text-2xl text-ink">{money ? `$${Number(value || 0).toLocaleString()}` : Number(value || 0).toLocaleString()}</p>
    </div>
  );
}
